import React from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import Button from "@amzn/meridian/button";
import Select, { SelectOption } from "@amzn/meridian/select";
import Tab, { TabGroup } from "@amzn/meridian/tab";
import Icon from "@amzn/meridian/icon";
import Toggle from "@amzn/meridian/toggle";
import Tag from "@amzn/meridian/tag";
import ButtonGroup, { ButtonOption } from "@amzn/meridian/button-group";
import Loader from "@amzn/meridian/loader";
import downloadLargeTokens from "@amzn/meridian-tokens/base/icon/download-large";
import filterTokens from "@amzn/meridian-tokens/base/icon/filter";
import { AuditResult } from "src/store/auditSlice";
import PaginationWithDropdown from "src/components/pages/audit/paginationWithDropdown";
import AuditTable from "./AuditTable";
import FilterModal from "./FilterModal";
import OverrideRateMetrics from "./OverrideRateMetrics";
import DQOMetrics from "./DQOMetrics";
import { useAuditDashboard } from "./useAuditDashboard";
import { TimePeriodType } from "src/services/timePeriodService";
import { useAppSelector } from "src/store/store";
import { ADMIN_LDAP_GROUP, ENGINEER_LDAP_GROUP } from "src/constants";
import "./auditDashboard.scss";

const AuditDashboard = () => {
  // Get user LDAP groups for permission checking
  const { ldap } = useAppSelector((state) => state.user);

  // Check if user can see metrics tabs (admin and engineer only, not CPT admin)
  const canSeeMetricsTabs = ldap.includes(ADMIN_LDAP_GROUP) || ldap.includes(ENGINEER_LDAP_GROUP);

  const {
    // State
    currentTab,
    setCurrentTab,
    showAllFields,
    setShowAllFields,
    includeAllFields,
    setIncludeAllFields,
    sortColumn,
    sortDirection,
    dateRange,
    setDateRange,
    selectedFields,
    setSelectedFields,
    selectedAuditResults,
    setSelectedAuditResults,
    selectedAISources,
    setSelectedAISources,
    selectedSiteTypes,
    setSelectedSiteTypes,
    selectedSites,
    setSelectedSites,
    selectedBusinessUnits,
    setSelectedBusinessUnits,
    selectedOrganizations,
    setSelectedOrganizations,
    selectedScopes,
    setSelectedScopes,
    isFilterModalOpen,
    setIsFilterModalOpen,
    allIncidents,
    filteredIncidents,
    visibleIncidents,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    numberOfPages,
    selectedRows,
    setSelectedRows,
    selectedPeriodType,
    setSelectedPeriodType,
    selectedPeriodIndex,
    setSelectedPeriodIndex,
    filterMode,
    selectedWorkflowIds,
    setSelectedWorkflowIds,
    auditJobs,
    exportFilteredAuditDataByIdsLoading,
    exportFilteredAuditDataLoading,

    // Functions
    onSort,
    toggleRowSelection,
    handleExport,
    resetFilters,
    performSearch,
    handleFilterModeChange,
    getFilteredAuditDataLoading,
  } = useAuditDashboard();

  // Generate period options based on selected period type
  const generatePeriodOptions = () => {
    const options = [];
    const now = new Date();

    switch (selectedPeriodType) {
      case TimePeriodType.WEEKLY:
        // Generate last 12 weeks (Sunday to Saturday, counting from start of 2025)
        for (let i = 0; i < 12; i++) {
          const currentDate = new Date(now.getTime() - i * 7 * 24 * 60 * 60 * 1000);
          // Find the Sunday of this week
          const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 1 = Monday, etc.
          const weekStart = new Date(currentDate.getTime() - dayOfWeek * 24 * 60 * 60 * 1000);
          const weekEnd = new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000); // Saturday

          // Calculate week number from start of 2025
          const startOf2025 = new Date(2025, 0, 1); // January 1, 2025
          const daysSinceStartOf2025 = Math.floor(
            (weekStart.getTime() - startOf2025.getTime()) / (24 * 60 * 60 * 1000),
          );
          const weekNumber = Math.floor(daysSinceStartOf2025 / 7) + 1;

          options.push({
            index: i,
            periodStart: weekStart,
            periodEnd: weekEnd,
            label:
              i === 0
                ? `Current Week ${weekNumber} (${weekStart.toLocaleDateString()} - ${weekEnd.toLocaleDateString()})`
                : `Week ${weekNumber} (${weekStart.toLocaleDateString()} - ${weekEnd.toLocaleDateString()})`,
          });
        }
        break;
      case TimePeriodType.MONTHLY:
        // Generate last 12 months (from 1st of month to today for current month, full month for past months)
        for (let i = 0; i < 12; i++) {
          const monthStart = new Date(now.getFullYear(), now.getMonth() - i, 1);
          let monthEnd;
          if (i === 0) {
            // Current month: from 1st to today
            monthEnd = now;
          } else {
            // Past months: full month
            monthEnd = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
          }
          options.push({
            index: i,
            periodStart: monthStart,
            periodEnd: monthEnd,
            label: `${monthStart.toLocaleDateString("en-US", { month: "short" })} ${monthStart.getFullYear()}`,
          });
        }
        break;
      case TimePeriodType.QUARTERLY:
        // Generate last 4 quarters
        for (let i = 0; i < 4; i++) {
          const currentQuarter = Math.floor(now.getMonth() / 3);
          const quarterStart = new Date(now.getFullYear(), (currentQuarter - i) * 3, 1);
          const quarterEnd = new Date(now.getFullYear(), (currentQuarter - i + 1) * 3, 0);
          options.push({
            index: i,
            periodStart: quarterStart,
            periodEnd: quarterEnd,
            label: `Q${currentQuarter - i + 1} ${quarterStart.getFullYear()}`,
          });
        }
        break;
      case TimePeriodType.YEARLY:
        // Generate last 3 years
        for (let i = 0; i < 3; i++) {
          const yearStart = new Date(now.getFullYear() - i, 0, 1);
          const yearEnd = new Date(now.getFullYear() - i, 11, 31);
          options.push({
            index: i,
            periodStart: yearStart,
            periodEnd: yearEnd,
            label: `${now.getFullYear() - i}`,
          });
        }
        break;
    }
    return options;
  };

  const periodOptions = generatePeriodOptions();

  const handlePeriodTypeChange = (periodType: TimePeriodType) => {
    setSelectedPeriodType(periodType);
    setSelectedPeriodIndex(0); // Reset to first option
  };

  const handlePeriodIndexChange = (index: number) => {
    setSelectedPeriodIndex(index);
  };

  // Toggle all rows selection for different tab types
  const getToggleAllRowsHandler = (tabType: "all" | "human" | "ai") => {
    return () => {
      let targetIncidents = filteredIncidents;

      if (tabType === "human") {
        targetIncidents = filteredIncidents.filter((incident) =>
          incident.fields.some(
            (field) =>
              field.auditResult === AuditResult.EHS_SMART_ASSISTANT_CORRECT ||
              field.auditResult === AuditResult.LLM_CORRECT,
          ),
        );
      } else if (tabType === "ai") {
        targetIncidents = filteredIncidents.filter((incident) =>
          incident.fields.some(
            (field) =>
              field.auditResult === AuditResult.HUMAN_CORRECT ||
              field.auditResult === AuditResult.NEITHER,
          ),
        );
      }

      if (selectedRows.length === targetIncidents.length) {
        setSelectedRows([]);
      } else {
        setSelectedRows(targetIncidents.map((item) => item.incidentId));
      }
    };
  };

  return (
    <Column spacingInset={"500"} spacing={"400"}>
      {/* Header */}
      <Row>
        <Column alignmentHorizontal="start">
          <Text type="d50">Audit Analyzer</Text>
        </Column>
      </Row>

      {/* Tab Navigation and Tables */}
      <Column>
        <TabGroup value={currentTab} onChange={setCurrentTab} aria-labelledby="tablist-heading">
          {/* DQO Metrics Tab - Only visible to regular admin and engineer */}
          {canSeeMetricsTabs && (
            <Tab value="tab-dqo-metrics" aria-controls="tabpanel-dqo-metrics" id="tab-dqo-metrics">
              DQO Metrics
            </Tab>
          )}
          {/* Incident Override Metrics Tab - Only visible to regular admin and engineer */}
          {canSeeMetricsTabs && (
            <Tab
              value="tab-incident-metrics"
              aria-controls="tabpanel-incident-metrics"
              id="tab-incident-metrics"
            >
              Incident Override Metrics
            </Tab>
          )}
          {/* View Audits Tab - Visible to all authorized users */}
          <Tab value="tab-all-audits" aria-controls="tabpanel-all-audits" id="tab-all-audits">
            {currentTab === "tab-all-audits"
              ? `View Audits (${filteredIncidents.length})`
              : "View Audits"}
          </Tab>
          {/* Selected Audits Tab - Always visible */}
          <Tab
            value="tab-selected-audits"
            aria-controls="tabpanel-selected-audits"
            id="tab-selected-audits"
          >
            Selected Audits ({selectedRows.length})
          </Tab>
        </TabGroup>

        {/* Incident Metrics Tab - Only show if user has permissions */}
        {canSeeMetricsTabs && currentTab === "tab-incident-metrics" && (
          <div
            role="tabpanel"
            id="tabpanel-incident-metrics"
            aria-labelledby="tab-incident-metrics"
          >
            <OverrideRateMetrics
              periodType={selectedPeriodType.toLowerCase()}
              year={new Date().getFullYear()}
            />
          </div>
        )}

        {/* DQO Metrics Tab - Only show if user has permissions */}
        {canSeeMetricsTabs && currentTab === "tab-dqo-metrics" && (
          <div role="tabpanel" id="tabpanel-dqo-metrics" aria-labelledby="tab-dqo-metrics">
            <DQOMetrics
              periodType={selectedPeriodType.toLowerCase()}
              year={new Date().getFullYear()}
            />
          </div>
        )}

        {/* All Audits Table */}
        {currentTab === "tab-all-audits" && (
          <div role="tabpanel" id="tabpanel-all-audits" aria-labelledby="tab-all-audits">
            <Column spacing="400">
              {/* Search Row */}
              <Row alignmentVertical="center" spacing="300">
                <Column spacing="200" minWidth={150}>
                  <Text type="b200">Search by</Text>
                  <Select
                    value={filterMode}
                    onChange={handleFilterModeChange}
                    placeholder="Select search method..."
                    width={250}
                  >
                    <SelectOption value="date" label="Incident reported date" />
                    <SelectOption value="workflow" label="Submitted audit job" />
                  </Select>
                </Column>

                {/* Conditional search controls based on selected search method */}
                {filterMode === "date" ? (
                  <Row spacing="300" alignmentVertical="bottom">
                    <Select
                      label="Period Type"
                      value={selectedPeriodType}
                      onChange={handlePeriodTypeChange}
                      placeholder="Enter value..."
                      width={150}
                    >
                      {[TimePeriodType.WEEKLY, TimePeriodType.MONTHLY].map((option) => (
                        <SelectOption
                          key={option}
                          value={option}
                          label={option.charAt(0).toUpperCase() + option.slice(1)}
                        />
                      ))}
                    </Select>
                    <Select
                      label="Period"
                      value={selectedPeriodIndex}
                      onChange={handlePeriodIndexChange}
                      placeholder="Select period..."
                      width={200}
                    >
                      {periodOptions.map((option) => (
                        <SelectOption
                          key={option.index}
                          value={option.index}
                          label={option.label}
                        />
                      ))}
                    </Select>
                    <Button type="primary" onClick={performSearch}>
                      Search
                    </Button>
                  </Row>
                ) : (
                  <Row spacing="300" alignmentVertical="bottom">
                    <Select
                      label="Select Audit Jobs"
                      value={selectedWorkflowIds}
                      onChange={setSelectedWorkflowIds}
                      placeholder="Select audit jobs..."
                      width={400}
                    >
                      {auditJobs.map((job: any) => {
                        const reviewPercent =
                          job.linesProcessed > 0
                            ? Math.round((job.reviewed_count / job.linesProcessed) * 100)
                            : 0;
                        return (
                          <SelectOption
                            key={job.id}
                            value={job.id}
                            label={`${job.inputFileName} (${new Date(job.timestamp).toLocaleDateString()}) - ${reviewPercent}% (${job.reviewed_count}/${job.linesProcessed})`}
                          />
                        );
                      })}
                    </Select>
                    <Button type="primary" onClick={performSearch}>
                      Search
                    </Button>
                  </Row>
                )}
              </Row>

              {/* Filter Row */}
              <Row alignmentVertical="center" spacing="300" widths={["60%", "40%"]}>
                <Row spacing="300" alignmentVertical="bottom">
                  <Column spacing="200">
                    <Text type="b200">Defect Type</Text>
                    <ButtonGroup
                      value={selectedAuditResults}
                      onChange={allIncidents.length ? setSelectedAuditResults : () => {}}
                      type="outline"
                    >
                      <ButtonOption value="AI Defect" disabled={!allIncidents.length}>
                        AI Defect
                      </ButtonOption>
                      <ButtonOption value="Human Defect" disabled={!allIncidents.length}>
                        Human Defect
                      </ButtonOption>
                      <ButtonOption value="Both Defect" disabled={!allIncidents.length}>
                        Both Defect
                      </ButtonOption>
                    </ButtonGroup>
                  </Column>
                  <Column spacing="200">
                    <Text type="b200">AI Source</Text>
                    <ButtonGroup
                      value={selectedAISources}
                      onChange={allIncidents.length ? setSelectedAISources : () => {}}
                      type="outline"
                    >
                      <ButtonOption value="LLM" disabled={!allIncidents.length}>
                        LLM
                      </ButtonOption>
                      <ButtonOption value="EHS Smart Assistant" disabled={!allIncidents.length}>
                        EHS Smart Assistant
                      </ButtonOption>
                    </ButtonGroup>
                  </Column>
                  <Column spacing="200">
                    <Text type="b200">Business Unit</Text>
                    <Select
                      value={selectedBusinessUnits}
                      onChange={setSelectedBusinessUnits}
                      placeholder="Select BU..."
                      width={150}
                      disabled={!allIncidents.length}
                    >
                      {allIncidents
                        .map((incident) => incident.obr_bu)
                        .filter((bu, index, self) => bu && self.indexOf(bu) === index)
                        .sort()
                        .map((bu) => (
                          <SelectOption key={bu} value={bu} label={bu} />
                        ))}
                    </Select>
                  </Column>
                  <Column spacing="200">
                    <Text type="b200">Organization</Text>
                    <Select
                      value={selectedOrganizations}
                      onChange={setSelectedOrganizations}
                      placeholder="Select org..."
                      width={150}
                      disabled={!allIncidents.length}
                    >
                      {allIncidents
                        .map((incident) => incident.obr_org)
                        .filter((org, index, self) => org && self.indexOf(org) === index)
                        .sort()
                        .map((org) => (
                          <SelectOption key={org} value={org} label={org} />
                        ))}
                    </Select>
                  </Column>
                  <Column spacing="200">
                    <Text type="b200">Scope</Text>
                    <Select
                      value={selectedScopes}
                      onChange={setSelectedScopes}
                      placeholder="Select scope..."
                      width={150}
                      disabled={!allIncidents.length}
                    >
                      {allIncidents
                        .map((incident) => incident.obr_scope)
                        .filter((scope, index, self) => scope && self.indexOf(scope) === index)
                        .sort()
                        .map((scope) => (
                          <SelectOption key={scope} value={scope} label={scope} />
                        ))}
                    </Select>
                  </Column>
                </Row>
                <Row alignmentHorizontal="end" spacing="300" alignmentVertical="bottom">
                  <Column spacing="200" alignmentVertical="center" minWidth={110}>
                    <Text type="b200">Show Core Fields</Text>
                    <Toggle
                      checked={!includeAllFields}
                      onChange={(value) => setIncludeAllFields(!value)}
                      size="large"
                      disabled={!allIncidents.length}
                    />
                  </Column>
                  <Column spacing="200" alignmentVertical="center" minWidth={110}>
                    <Text type="b200">Expand All Rows</Text>
                    <Toggle checked={showAllFields} onChange={setShowAllFields} size="large" />
                  </Column>
                  <Button
                    type="tertiary"
                    onClick={() => setIsFilterModalOpen(true)}
                    disabled={!allIncidents.length}
                  >
                    <Icon tokens={filterTokens} />
                    More Filters
                  </Button>
                  <Button
                    type="tertiary"
                    onClick={handleExport}
                    disabled={
                      !allIncidents.length ||
                      getFilteredAuditDataLoading === "pending" ||
                      exportFilteredAuditDataByIdsLoading === "pending" ||
                      exportFilteredAuditDataLoading === "pending"
                    }
                  >
                    {exportFilteredAuditDataByIdsLoading === "pending" ||
                    exportFilteredAuditDataLoading === "pending" ? (
                      <Row spacing="200" alignmentVertical="center">
                        <Loader size="small" />
                        <Text>Exporting...</Text>
                      </Row>
                    ) : (
                      <>
                        <Icon tokens={downloadLargeTokens} />
                        {selectedRows.length > 0
                          ? `Export Selected (${selectedRows.length})`
                          : "Export"}
                      </>
                    )}
                  </Button>
                </Row>
              </Row>

              {/* Active Filters Tags */}
              {(selectedFields.length > 0 ||
                selectedAuditResults.length > 0 ||
                selectedAISources.length > 0 ||
                selectedSiteTypes.length > 0 ||
                selectedSites.length > 0 ||
                selectedBusinessUnits.length > 0 ||
                selectedOrganizations.length > 0 ||
                selectedScopes.length > 0) && (
                <Row spacing="200" alignmentVertical="center">
                  <Text type="b200" color="secondary">
                    Active Filters:
                  </Text>
                  {selectedFields.map((field) => (
                    <Tag
                      key={field}
                      onClose={() => setSelectedFields(selectedFields.filter((f) => f !== field))}
                    >
                      {field}
                    </Tag>
                  ))}
                  {selectedAuditResults.map((result) => (
                    <Tag
                      key={result}
                      onClose={() =>
                        setSelectedAuditResults(selectedAuditResults.filter((r) => r !== result))
                      }
                    >
                      {result}
                    </Tag>
                  ))}
                  {selectedAISources.map((source) => (
                    <Tag
                      key={`ai-source-${source}`}
                      onClose={() =>
                        setSelectedAISources(selectedAISources.filter((s) => s !== source))
                      }
                    >
                      {`AI Source: ${source}`}
                    </Tag>
                  ))}
                  {selectedSiteTypes.map((siteType: string) => {
                    const siteTypeLabel = `Site Type: ${siteType}`;
                    return (
                      <Tag
                        key={`sitetype-${siteType}`}
                        onClose={() =>
                          setSelectedSiteTypes(
                            selectedSiteTypes.filter((o: string) => o !== siteType),
                          )
                        }
                      >
                        {siteTypeLabel}
                      </Tag>
                    );
                  })}
                  {selectedSites.map((site) => {
                    const siteLabel = `Site: ${site}`;
                    return (
                      <Tag
                        key={`site-${site}`}
                        onClose={() => setSelectedSites(selectedSites.filter((s) => s !== site))}
                      >
                        {siteLabel}
                      </Tag>
                    );
                  })}
                  {selectedBusinessUnits.map((bu) => {
                    const buLabel = `Business Unit: ${bu}`;
                    return (
                      <Tag
                        key={`bu-${bu}`}
                        onClose={() =>
                          setSelectedBusinessUnits(selectedBusinessUnits.filter((b) => b !== bu))
                        }
                      >
                        {buLabel}
                      </Tag>
                    );
                  })}
                  {selectedOrganizations.map((org) => {
                    const orgLabel = `Organization: ${org}`;
                    return (
                      <Tag
                        key={`org-${org}`}
                        onClose={() =>
                          setSelectedOrganizations(selectedOrganizations.filter((o) => o !== org))
                        }
                      >
                        {orgLabel}
                      </Tag>
                    );
                  })}
                  {selectedScopes.map((scope) => {
                    const scopeLabel = `Scope: ${scope}`;
                    return (
                      <Tag
                        key={`scope-${scope}`}
                        onClose={() => setSelectedScopes(selectedScopes.filter((s) => s !== scope))}
                      >
                        {scopeLabel}
                      </Tag>
                    );
                  })}
                </Row>
              )}

              {getFilteredAuditDataLoading === "pending" ? (
                <Column alignmentHorizontal="center" spacing="400" spacingInset="800">
                  <Loader size="large" />
                  <Text type="b200" color="secondary">
                    Loading audit data...
                  </Text>
                </Column>
              ) : filteredIncidents.length > 0 ? (
                <AuditTable
                  incidents={filteredIncidents}
                  visibleIncidents={visibleIncidents}
                  selectedRows={selectedRows}
                  onToggleRowSelection={toggleRowSelection}
                  onToggleAllRows={getToggleAllRowsHandler("all")}
                  showAllFields={showAllFields}
                  includeAllFields={includeAllFields}
                  onSort={onSort}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  tabType="all"
                  caption="All Audit Data"
                  selectedBusinessUnits={selectedBusinessUnits}
                  selectedOrganizations={selectedOrganizations}
                  selectedScopes={selectedScopes}
                  selectedSiteTypes={selectedSiteTypes}
                  selectedSites={selectedSites}
                />
              ) : (
                <Column alignmentHorizontal="center" spacing="400" spacingInset="800">
                  <Text type="h300" color="secondary">
                    No audit data found
                  </Text>
                  <Text type="b200" color="secondary">
                    No audit data available for the selected date range. Try selecting a different
                    period or check if data exists for this time frame.
                  </Text>
                </Column>
              )}
            </Column>
          </div>
        )}

        {/* Selected Audits Tab - Always visible */}
        {currentTab === "tab-selected-audits" && (
          <div role="tabpanel" id="tabpanel-selected-audits" aria-labelledby="tab-selected-audits">
            <Column spacing="400">
              {/* Export Button for Selected Audits */}
              <Row alignmentHorizontal="end" alignmentVertical="center">
                <Row spacing="300" alignmentVertical="center">
                  <Button
                    type="secondary"
                    onClick={() => setSelectedRows([])}
                    disabled={selectedRows.length === 0}
                  >
                    Clear Selection
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleExport}
                    disabled={
                      selectedRows.length === 0 ||
                      exportFilteredAuditDataByIdsLoading === "pending" ||
                      exportFilteredAuditDataLoading === "pending"
                    }
                  >
                    {exportFilteredAuditDataByIdsLoading === "pending" ||
                    exportFilteredAuditDataLoading === "pending" ? (
                      <Row spacing="200" alignmentVertical="center">
                        <Loader size="small" />
                        <Text>Exporting...</Text>
                      </Row>
                    ) : (
                      <>
                        <Icon tokens={downloadLargeTokens} />
                        {selectedRows.length > 0
                          ? `Export Selected (${selectedRows.length})`
                          : "Export Selected"}
                      </>
                    )}
                  </Button>
                </Row>
              </Row>

              {/* Always show table - create selected incidents from selectedRows */}
              {(() => {
                if (selectedRows.length === 0) {
                  return (
                    <Column alignmentHorizontal="center" spacing="400" spacingInset="800">
                      <Text type="h300" color="secondary">
                        No audits selected
                      </Text>
                      <Text type="b200" color="secondary">
                        Go to the "View Audits" tab to browse and select audits for export. Your
                        selections will be saved and persist across page refreshes.
                      </Text>
                    </Column>
                  );
                }

                // Create incident objects for selected rows (showing all selected, not just current time range)
                const selectedIncidents = selectedRows.map((incidentId) => {
                  const foundIncident = filteredIncidents.find(
                    (incident) => incident.incidentId === incidentId,
                  );
                  return (
                    foundIncident || {
                      incidentId,
                      caseId: incidentId, // Fallback if not found in current data
                      timestamp: new Date().toISOString(),
                      dateReported: new Date().toISOString(),
                      auditWorkflowId: "",
                      siteType: "",
                      site: "",
                      reviewer: "",
                      dqoCount: 0,
                      aiDefectCount: 0,
                      aiSource: "",
                      obr_bu: "",
                      obr_org: "",
                      obr_scope: "",
                      fields: [],
                    }
                  );
                });

                return (
                  <AuditTable
                    incidents={selectedIncidents}
                    visibleIncidents={selectedIncidents}
                    selectedRows={selectedRows}
                    onToggleRowSelection={toggleRowSelection}
                    onToggleAllRows={() => setSelectedRows([])}
                    showAllFields={showAllFields}
                    includeAllFields={includeAllFields}
                    onSort={onSort}
                    sortColumn={sortColumn}
                    sortDirection={sortDirection}
                    tabType="all"
                    caption="Selected Audits"
                    selectedBusinessUnits={selectedBusinessUnits}
                    selectedOrganizations={selectedOrganizations}
                    selectedScopes={selectedScopes}
                    selectedSiteTypes={selectedSiteTypes}
                    selectedSites={selectedSites}
                  />
                );
              })()}
            </Column>
          </div>
        )}

        {/* Pagination - Hide for metrics tabs */}
        {currentTab === "tab-all-audits" && (
          <PaginationWithDropdown
            currentPage={currentPage}
            totalPages={numberOfPages}
            perPage={itemsPerPage}
            setPerPage={(pageNum) => {
              setItemsPerPage(pageNum);
              setCurrentPage(1);
            }}
            setCurrentPage={setCurrentPage}
          />
        )}
      </Column>

      {/* Filter Modal */}
      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        dateRange={dateRange}
        setDateRange={setDateRange}
        selectedFields={selectedFields}
        setSelectedFields={setSelectedFields}
        selectedAuditResults={selectedAuditResults}
        setSelectedAuditResults={setSelectedAuditResults}
        selectedSiteTypes={selectedSiteTypes}
        setSelectedSiteTypes={setSelectedSiteTypes}
        selectedSites={selectedSites}
        setSelectedSites={setSelectedSites}
        selectedBusinessUnits={selectedBusinessUnits}
        setSelectedBusinessUnits={setSelectedBusinessUnits}
        selectedOrganizations={selectedOrganizations}
        setSelectedOrganizations={setSelectedOrganizations}
        selectedScopes={selectedScopes}
        setSelectedScopes={setSelectedScopes}
        incidents={allIncidents}
        onReset={resetFilters}
      />
    </Column>
  );
};

export default AuditDashboard;
